import { z } from 'zod';
/**
 * The activity contract.
 *
 * Content comes from parameterised ENGINES, never hand-authored worksheets: a
 * small number of generators produce unlimited instances from curriculum
 * parameters and interest themes.
 *
 * Two rules shape these types:
 *
 *  1. The answer never leaves the server. A question travels with an opaque,
 *     signed token; the client posts the child's response and the SERVER checks
 *     it. An answer key in the browser is an answer key a child can find.
 *  2. Checking is code, always. No engine may consult a language model to
 *     decide whether a child was right - not for maths, not for spelling, not
 *     for anything.
 */
export const ASSIST_LEVEL_VALUES = ['independent', 'hint', 'retry'];
/** Every prompt shape the child UI knows how to render. */
export const promptSchema = z.discriminatedUnion('kind', [
    /**
     * Vertical addition, written the way it is written on paper: one number
     * above the other, a box above each column for the digit carried into it,
     * and a row of cells for the answer.
     *
     * The only prompt with no `choices`. Every other activity offers a short list
     * to pick from; here the child builds the number a digit at a time, because
     * choosing "334" from four options is not the skill. The skill is working
     * right to left and remembering the carried one.
     *
     * `answerCells` is deliberately one wider than the longest addend, so the
     * child decides whether the answer needs that extra column. Sizing it to the
     * real answer would give away whether the total passes a thousand.
     */
    z.object({
        kind: z.literal('columnAddition'),
        instruction: z.string(),
        top: z.number().int().min(1).max(9999),
        bottom: z.number().int().min(1).max(9999),
        answerCells: z.number().int().min(2).max(5),
    }),
    /** Count the things, choose the numeral. */
    z.object({
        kind: z.literal('countAndChoose'),
        instruction: z.string(),
        itemGlyph: z.string(),
        itemLabel: z.string(),
        count: z.number().int().min(1).max(30),
        /**
         * How the things are arranged.
         *
         * 'scattered' is for counting, where touching each one in turn is the
         * skill. 'subitise' lays them out in the dice and pair patterns a child
         * can recognise WITHOUT counting, which is the whole point of subitising -
         * six dots in a straight line is a counting question wearing a
         * subitising question's name.
         */
        layout: z.enum(['scattered', 'subitise']).optional(),
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .min(2)
            .max(6),
    }),
    /** Sound tiles that blend into a word; choose the word. */
    z.object({
        kind: z.literal('blendChoose'),
        instruction: z.string(),
        graphemes: z.array(z.string()).min(2).max(5),
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .min(2)
            .max(4),
    }),
    /**
     * A decodable mini-book, then a question about it. Every word on every page
     * has passed the decodability validator for THIS child; tapping a word plays
     * its recorded audio where one exists.
     */
    z.object({
        kind: z.literal('storyBook'),
        instruction: z.string(),
        title: z.string(),
        pages: z
            .array(z.object({ text: z.string(), glyph: z.string() }))
            .min(1)
            .max(8),
        question: z.string(),
        /** Set for an inference question, so the UI can ask "what told you that?" */
        inference: z.boolean().optional(),
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .min(2)
            .max(4),
    }),
    /** Build a word from sound tiles. The answer is the assembled word. */
    z.object({
        kind: z.literal('buildWord'),
        instruction: z.string(),
        audioKey: z.string().optional(),
        /** Shown as a picture clue when there is no audio for the word. */
        glyph: z.string().optional(),
        tiles: z.array(z.string()).min(3).max(10),
        slots: z.number().int().min(2).max(6),
        /** Sent as the choiceId once assembled; listed so the schema stays uniform. */
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .max(0)
            .default([]),
    }),
    /**
     * The general maths prompt: an instruction, an optional thing to look at,
     * and choices. One shape covers number sentences, ten frames, arrays, clock
     * faces, coins and graphs, so the child meets a consistent way of answering
     * across every maths engine.
     */
    z.object({
        kind: z.literal('pickAnswer'),
        instruction: z.string(),
        /**
         * How the child gives their answer.
         *
         * 'choice' shows the options. 'keypad' sends NO options and the child
         * types the number - which is the point: picking the right answer from
         * four is a different skill from knowing it, and a capable child answers
         * multiple choice in two seconds without doing the maths.
         */
        answerFormat: z.enum(['choice', 'keypad']).optional(),
        /**
         * The same sum written one number above the other, with somewhere to put
         * the carried digit.
         *
         * Set only when the question is a written method - a child doing 47 + 28
         * in their head does not need columns, and a child learning to carry
         * cannot do it on one line. Rendered only at the keypad, because carry
         * boxes are for working an answer out, not for picking one.
         */
        stacked: z
            .object({
            top: z.number().int().min(0),
            bottom: z.number().int().min(0),
            operator: z.enum(['+', '−']),
            answerCells: z.number().int().min(1).max(5),
        })
            .optional(),
        /** A number sentence, numeral or pattern shown large. */
        display: z.string().optional(),
        /** Ten frames, arrays and equal groups. `filled` counts from the start. */
        grid: z
            .object({
            rows: z.number().int().min(1).max(10),
            cols: z.number().int().min(1).max(10),
            glyph: z.string(),
            filled: z.number().int().min(0).max(100).optional(),
            /**
             * Draw it as a real ten frame: outlined cells, five to a row, with the
             * rows of five visibly separated.
             *
             * Without it the frame was a loose row of dots, so "the top row holds
             * five" - the entire reason a ten frame exists - was not true of what
             * was on screen.
             */
            frame: z.boolean().optional(),
            /**
             * The empty cells are things HIDDEN, not space left over.
             *
             * Part-whole asks "how many are hiding", so the cells that are not
             * filled have to look like something is under them rather than like
             * nothing is there.
             */
            hidden: z.boolean().optional(),
        })
            .optional(),
        /**
         * The choices are shapes, so draw them instead of naming them.
         *
         * Rendered as words, "rectangle" and "square" are a reading question, and
         * the rectangle on the contact sheet was the size of a hyphen.
         */
        shapes: z.boolean().optional(),
        /**
         * Things placed relative to each other, for position words.
         *
         * `maths.position-direction` is "use words like above, below, beside and
         * behind", and nothing in the app could ask it - the skill was hinted at
         * shape-sorter, which has no position mode, so it accrued mastery for
         * naming shapes. Position cannot be asked in words alone: "where is the
         * cat?" needs a cat somewhere.
         */
        scene: z
            .object({
            rows: z.number().int().min(1).max(3),
            cols: z.number().int().min(1).max(3),
            items: z
                .array(z.object({
                row: z.number().int().min(1).max(3),
                col: z.number().int().min(1).max(3),
                glyph: z.string(),
                label: z.string(),
                /** The thing being asked about, drawn with emphasis. */
                subject: z.boolean().optional(),
            }))
                .min(2)
                .max(3),
        })
            .optional(),
        /**
         * Two things side by side, compared by a measurable attribute.
         *
         * `maths.compare-length` and `maths.compare-mass-capacity` were both
         * hinted at compare-order, which compares NUMERALS - so "which is longer"
         * was being answered by "which number is bigger", and a child could
         * complete both skills without ever comparing two objects.
         */
        compare: z
            .object({
            attribute: z.enum(['length', 'mass', 'capacity']),
            left: z.object({ glyph: z.string(), label: z.string(), size: z.number().min(1).max(10) }),
            right: z.object({ glyph: z.string(), label: z.string(), size: z.number().min(1).max(10) }),
        })
            .optional(),
        /** Australian coins and notes, as labels: "50c", "$2". */
        money: z.array(z.string()).optional(),
        clock: z
            .object({ hour: z.number().int().min(1).max(12), minute: z.number().int().min(0).max(59) })
            .optional(),
        /** Picture graph: one row per category. */
        graph: z
            .array(z.object({ label: z.string(), glyph: z.string(), count: z.number().int().min(0).max(12) }))
            .optional(),
        /**
         * Empty when `answerFormat` is 'keypad'.
         *
         * The options are not merely hidden - they are not sent, because the
         * correct answer is among them and an answer key in the browser is an
         * answer key a child eventually finds. Engines still always produce at
         * least two; a test asserts that, which is where the guarantee belongs.
         */
        choices: z.array(z.object({ id: z.string(), label: z.string() })).max(6),
    }),
    /**
     * Trace a letter or numeral. The strokes carry their own start dots and
     * arrows; the child's finger path is checked against them on the SERVER, so
     * "you formed that correctly" means something.
     */
    z.object({
        kind: z.literal('traceLetter'),
        instruction: z.string(),
        character: z.string().min(1).max(2),
        chant: z.string(),
        level: z.enum(['guided', 'faded', 'free']),
        strokes: z.array(z.object({
            points: z.array(z.object({ x: z.number(), y: z.number() })).min(2),
            hint: z.string(),
        })),
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .max(0)
            .default([]),
    }),
    /** Put words or pictures in the right order. */
    z.object({
        kind: z.literal('orderItems'),
        instruction: z.string(),
        /** Shown shuffled; the child drags or taps them into order. */
        items: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .min(2)
            .max(8),
        /** True when the items are pictures rather than words. */
        pictures: z.boolean().optional(),
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .max(0)
            .default([]),
    }),
    /**
     * Free writing. Never auto-scored - it goes to a grown-up, and the screen
     * says so, because pretending to mark a child's story would be a lie.
     */
    z.object({
        kind: z.literal('freeWriting'),
        instruction: z.string(),
        promptText: z.string(),
        allowDrawing: z.boolean(),
        /**
         * The editing pass, in the child's own hands.
         *
         * "Improve their written work through drafting and editing" is named
         * explicitly for Years 1-3, and this is the only honest way for an app to
         * offer it: mechanical, checkable things the CHILD looks for in their own
         * writing. Nothing here is scored, and ticking none of them still submits -
         * the app has no way to judge whether a capital letter is really there, and
         * pretending otherwise would be the lie the whole writing flow avoids.
         */
        checklist: z.array(z.string()).max(4).default([]),
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .max(0)
            .default([]),
    }),
    /** Hear a sound, choose the grapheme (or the reverse). */
    z.object({
        kind: z.literal('audioChoose'),
        instruction: z.string(),
        audioKey: z.string(),
        choices: z
            .array(z.object({ id: z.string(), label: z.string() }))
            .min(2)
            .max(4),
    }),
]);
/** What the server hands the client for one question. */
export const activityQuestion = z.object({
    /** Stable for this question instance; used for the attempt id seed. */
    questionId: z.string(),
    engineKey: z.string(),
    skillKey: z.string(),
    skillName: z.string(),
    difficultyBand: z.number().int().min(1).max(6),
    prompt: promptSchema,
    /** Shown only when the child asks, and it costs them the independent score. */
    hint: z.string(),
    /** Opaque and signed. Carries the answer; the client cannot read or forge it. */
    token: z.string(),
    step: z
        .object({
        kind: z.string(),
        index: z.number().int().min(0),
        total: z.number().int().min(0),
    })
        .optional(),
    /**
     * True when this one is past the end of today's plan - a child who chose to
     * keep exploring. The screen says "extra" rather than showing a progress bar
     * that finished several questions ago.
     */
    extra: z.boolean().optional(),
    /**
     * What the iPad may say out loud (read-aloud). Decided on the server per
     * engine, so a word the child is learning to read is never spoken. `choices`
     * is the spoken form of each choice in order, absent when the choices are
     * the reading.
     */
    speak: z.object({ text: z.string(), choices: z.array(z.string()).optional() }).optional(),
});
export const answerInput = z.object({
    /** Generated by the client so a retried POST is idempotent. */
    attemptId: z.string().uuid(),
    token: z.string(),
    /** The id of the choice the child tapped, or the answer they assembled. */
    choiceId: z.string().max(400),
    /**
     * A traced letter: one path per stroke, in the order they were drawn.
     * Checked on the SERVER, so "you formed that correctly" is a real claim.
     */
    traced: z
        .array(z.array(z.object({ x: z.number().min(-1).max(2), y: z.number().min(-1).max(2) })).max(600))
        .max(6)
        .optional(),
    /** A child's own writing. Never scored by the app - it goes to a grown-up. */
    writing: z
        .object({
        text: z.string().max(4000).optional(),
        drawing: z.string().max(400_000).optional(),
    })
        .optional(),
    assistLevel: z.enum(ASSIST_LEVEL_VALUES),
    latencyMs: z.number().int().min(0).max(600_000),
    sessionId: z.string().uuid().optional(),
});
/**
 * What the child is shown when a wrong answer means something specific.
 *
 * The app already works out WHY an answer was wrong - it has done since the
 * misconception tags were built - but until now that only ever reached the
 * parent dashboard. The child got "Not that one - try another", which is the
 * one thing that cannot help them: they already knew it was wrong.
 *
 * `line` names the pattern warmly. `steps` walk the repair, one short step at
 * a time, and deliberately stop short of stating the answer - the retry is
 * only worth offering if there is still something to work out. `focusColumn`
 * lets a column sum light up the column that actually went wrong, counted
 * from the ones.
 */
export const coaching = z.object({
    line: z.string().min(1),
    steps: z.array(z.string()).max(4).default([]),
    focusColumn: z.number().int().nonnegative().nullable().default(null),
});
/** How the app changes tack after repeated misses on one skill in one sitting. */
export const interventionStep = z.enum([
    'none',
    'easier-with-hint',
    'worked-example',
    'drop-to-prerequisite',
]);
export const answerResult = z.object({
    correct: z.boolean(),
    /** Warm, specific, and never punitive. Shown to the child. */
    feedback: z.string(),
    /** What the right answer was - only after the child has finished trying. */
    correctChoiceId: z.string().nullable(),
    /** True when the child should simply try this same question again. */
    retryOffered: z.boolean(),
    countedForMastery: z.boolean(),
    /** Teaching for THIS mistake. Null when the miss says nothing specific. */
    coach: coaching.nullable().default(null),
    /** Set once the app has changed tack rather than just asking again. */
    intervention: interventionStep.default('none'),
    /**
     * A game, OFFERED after a run of misses. Never started for them, and never
     * the only way to reach one - the same games come up in ordinary play, so
     * getting things wrong is not a route to the fun bit.
     */
    breather: z.object({ key: z.string(), name: z.string(), invitation: z.string() }).optional(),
});
/**
 * Warm feedback for a wrong answer. Never "wrong", never "incorrect", never a
 * red cross - Aubrey is derailed by those, and Archie does not need them
 * either. Every line here treats the miss as information and offers the retry.
 */
export const RETRY_LINES = [
    'Nearly! Have another go.',
    'Good thinking. Try once more.',
    "That's a tricky one. Let's try again.",
    'Not that one - try another.',
    'Have another look, you can do this.',
];
export const PRAISE_LINES = [
    'Yes! You worked that out.',
    'That is exactly right.',
    'Well done - you got it.',
    'Nice work. That one was right.',
    'You did it!',
];
/** Praise the effort and the strategy, never the child. */
export const PERSISTENCE_LINES = [
    'You kept going and you worked it out.',
    'You tried a different way and it worked.',
    'That took some thinking, and you did it.',
];
//# sourceMappingURL=activities.js.map